import { PricePoint, StockTransaction } from '../../hooks/useUniverse';

export interface PricePointWithTrades { date: string; close: number; buyPrice?: number; sellPrice?: number; }

const MAX_SNAP_BACK_DAYS = 5;

/** Previous calendar day via local Date parts: Date.parse of a bare YYYY-MM-DD is UTC and shifts a day in western timezones. */
const previousDay = (date: string): string => {
  const [y, m, d] = date.split('-').map(Number);
  const prev = new Date(y, m - 1, d - 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`;
};

/**
 * Merges trades into the price rows so ONE array can drive the whole chart:
 * the Line reads `close`, the marker Scatters read `buyPrice`/`sellPrice`.
 * Recharts requires this on a categorical XAxis — a per-Scatter `data` prop
 * makes the scatter's categories become the axis and the price line vanishes.
 *
 * A trade dated off the chart grid (weekend/holiday) snaps back to the nearest
 * earlier chart date, up to MAX_SNAP_BACK_DAYS; trades with no match are dropped.
 * Rows without trades carry no buyPrice/sellPrice keys, so Recharts renders no
 * point there. When multiple same-action trades snap to the same row, the last
 * one written wins (only one marker per action fits a row anyway).
 */
export function mergeTradesIntoPrices(
  prices: PricePoint[],
  transactions: StockTransaction[],
): PricePointWithTrades[] {
  const rows: PricePointWithTrades[] = prices.map((p) => ({ ...p }));
  const rowByDate = new Map(rows.map((row) => [row.date, row]));
  for (const tx of transactions) {
    if (tx.Action !== 'BUY' && tx.Action !== 'SELL') continue;
    let date = tx.Time.slice(0, 10);
    for (let step = 0; step <= MAX_SNAP_BACK_DAYS; step += 1) {
      const row = rowByDate.get(date);
      if (row) {
        if (tx.Action === 'BUY') row.buyPrice = tx.Price;
        else row.sellPrice = tx.Price;
        break;
      }
      date = previousDay(date);
    }
  }
  return rows;
}
